/**
 * **What the trainer may do about one session** — the action rows of the column's session panel
 * (T-A5), decided exactly as the mobile trainer sheet decides them
 * (`features/calendar/detail/model/sheetVariant.ts` `trainerActions`, ported table by table):
 *
 *   scheduled        → Przełóż · Napisz · Profil · Odwołaj
 *   emptySlot        → Napisz · Profil · Odwołaj
 *   inprogress       → Napisz · Profil
 *   terminal         → Przejrzyj sesję (a workout behind it) · Napisz · Profil
 *   missed           → Odwołaj · [Oznacz nieobecność] · Napisz · Profil   (a workout behind it)
 *                      [Oznacz nieobecność] · Napisz · Profil · Odwołaj     (nothing behind it)
 *   consultAwaiting  → Oznacz jako odbytą · Odwołaj · [Oznacz nieobecność] · Napisz · Profil
 *   skipped          → Napisz · Profil
 *   cancelled        → Napisz · Profil · [Anuluj obciążenie]
 *
 * The logger rows (Rozpocznij, z opóźnieniem, Wróć do sesji, Wypełnij slot) are NOT here — the web
 * runs no workout. "Napisz" stands where mobile puts it, before "Profil", but the web's chat is
 * mobile-only for now: the row is drawn and cannot act, and says why (owner, 25 September 2026).
 * "Przełóż" stands where mobile puts it, and only where the grid
 * lets the block move too (`sessionMove.canMove`, 1b): mobile also offers it on an empty slot, the
 * web's drag never did.
 *
 * One departure in emphasis: "Odwołaj" wears the destructive ink wherever it stands. Mobile draws
 * it plain in the overdue arms; on the web one act has one look.
 *
 * The bracketed rows are gated as mobile gates them: "Oznacz nieobecność" only on a stationary
 * session whose reserved time has fully ended (calendar-management R8 — the server refuses both),
 * and "Anuluj obciążenie" only when the caller says the charge may still be taken back (R14,
 * {@link chargeMayBeForgiven} plus a ledger read — see there).
 *
 * The state a session is in is read off the web's own status reading (`entities/calendar-event`'s
 * `occurrenceStatusOf`, carried on `BookingSession.status`), which is the same R14 classifier the
 * mobile sheet's `deriveSheetStatus` reads.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import { awaitsVerdict, type BookingSession } from './bookingSession';
import { consumesCredit } from './creditConsumption';
import { canMove } from './sessionMove';
import { shiftDay, wallReading, zonedMidnight } from './trainerClock';

export type SessionActionId =
  | 'reschedule'
  | 'message'
  | 'profile'
  | 'cancel'
  | 'markNoShow'
  | 'markHeld'
  | 'forgiveCharge'
  | 'review';

/** The rows that write, each asking the trainer to confirm first. */
export type ConfirmedAct = 'cancel' | 'markNoShow' | 'markHeld' | 'forgiveCharge';

export function isConfirmedAct(id: SessionActionId): id is ConfirmedAct {
  return id === 'cancel' || id === 'markNoShow' || id === 'markHeld' || id === 'forgiveCharge';
}

/** A row's emphasis — T-A5's plain row, and the red one for the call-off. */
export type SessionActionRole = 'primary' | 'default' | 'danger';

export interface SessionAction {
  readonly id: SessionActionId;
  readonly role: SessionActionRole;
}

/** Which of the mobile sheet's states a session is in. */
export type SessionState =
  | 'scheduled'
  | 'emptySlot'
  | 'inprogress'
  | 'terminal'
  | 'missed'
  | 'consultAwaiting'
  | 'skipped'
  | 'cancelled';

const MINUTE_MS = 60_000;

/** The state, as `deriveSheetStatus` names it — anything unreadable is read-only `cancelled`. */
export function sessionState(session: BookingSession, nowMs: number): SessionState {
  switch (session.status) {
    case 'emptySlot':
      return 'emptySlot';
    case 'live':
      return 'inprogress';
    case 'completed':
      return 'terminal';
    case 'missed':
      return 'missed';
    case 'skipped':
      return 'skipped';
    case 'scheduled':
      return awaitsVerdict(session, nowMs) ? 'consultAwaiting' : 'scheduled';
    case 'noShow':
    case 'cancelled':
    case 'lateCancelled':
    case null:
      return 'cancelled';
  }
}

/**
 * Whether the reserved time is wholly behind `nowMs` — the no-show gate (R8: start + length). An
 * occurrence with no hour reserved its whole day, so it waits for the day to end on the trainer's
 * clock.
 */
export function hasFullyEnded(session: BookingSession, nowMs: number, zone: string): boolean {
  if (session.hasHour && session.slotMs !== null) {
    return nowMs >= session.slotMs + session.durationMinutes * MINUTE_MS;
  }
  if (session.day === null) return false;
  return nowMs >= zonedMidnight(shiftDay(session.day, 1), zone);
}

/** The two rows every state closes its contact with, as mobile's `contactActions`: Napisz, Profil. */
const contact = (): SessionAction[] => [
  { id: 'message', role: 'default' },
  { id: 'profile', role: 'default' },
];

export interface ActionContext {
  readonly nowMs: number;
  readonly zone: string;
  /** The session cost a credit that is still standing and may be taken back (R14). */
  readonly chargeMayBeCancelled: boolean;
}

/** The panel's action rows for one session, in the order they stand. */
export function sessionActions(session: BookingSession, context: ActionContext): SessionAction[] {
  const { nowMs, zone } = context;
  // R8: a stationary session only (the server refuses a remote one), once its time has ended.
  const noShow: SessionAction[] =
    !session.isRemote && hasFullyEnded(session, nowMs, zone)
      ? [{ id: 'markNoShow', role: 'default' }]
      : [];
  const hasExecution = session.executionId !== null;

  switch (sessionState(session, nowMs)) {
    case 'scheduled':
      return [
        ...(canMove(session, nowMs) ? [{ id: 'reschedule', role: 'primary' } as const] : []),
        ...contact(),
        { id: 'cancel', role: 'danger' },
      ];
    case 'emptySlot':
      return [...contact(), { id: 'cancel', role: 'danger' }];
    case 'inprogress':
      return contact();
    case 'terminal':
      return hasExecution ? [{ id: 'review', role: 'primary' }, ...contact()] : contact();
    case 'missed':
      return hasExecution
        ? [{ id: 'cancel', role: 'danger' }, ...noShow, ...contact()]
        : [
            ...noShow.map((row) => ({ ...row, role: 'primary' as const })),
            ...contact(),
            { id: 'cancel', role: 'danger' },
          ];
    case 'consultAwaiting':
      return [
        { id: 'markHeld', role: 'primary' },
        { id: 'cancel', role: 'danger' },
        ...noShow,
        ...contact(),
      ];
    case 'skipped':
      return contact();
    case 'cancelled':
      return context.chargeMayBeCancelled
        ? [...contact(), { id: 'forgiveCharge', role: 'default' }]
        : contact();
  }
}

/** R14's two charges — the only ones a trainer is offered to take back. */
const DISPUTABLE: ReadonlySet<string> = new Set(['LateCancelled', 'NoShow']);

/** How far a charge is still argued about, in days either way — mobile's `FORGIVENESS_WINDOW_DAYS`. */
export const FORGIVENESS_WINDOW_DAYS = 90;

const DAY_MS = 86_400_000;

/**
 * May the trainer still cancel this session's charge? Mobile's `chargeMayBeForgiven`, ported: a late
 * cancellation or a no-show (the server accepts ANY consuming session, but R14 names these two and
 * mobile offers only them), that cost a credit (R10), within ninety days of today either way. A
 * session with no readable moment is not hidden by the window. Whether it was ALREADY forgiven is a
 * ledger fact — {@link isChargeForgiven}.
 */
export function chargeMayBeForgiven(session: BookingSession, nowMs: number, zone: string): boolean {
  if (session.wireStatus === null || !DISPUTABLE.has(session.wireStatus)) return false;
  if (
    !consumesCredit({
      origin: session.origin,
      status: session.wireStatus,
      carriesTrainer: session.carriesTrainer,
    })
  ) {
    return false;
  }
  const placed = session.slotMs ?? session.startMs;
  if (placed === null) return true;
  const midnight = zonedMidnight(wallReading(nowMs, zone).day, zone);
  const span = FORGIVENESS_WINDOW_DAYS * DAY_MS;
  return placed >= midnight - span && placed <= midnight + span;
}

/** Whether the ledger in hand already holds a forgiveness of this session. */
export function isChargeForgiven(
  movements: readonly { readonly kind: string | null; readonly calendarEventId?: number | null }[],
  sessionId: number,
): boolean {
  return movements.some(
    (movement) => movement.kind === 'forgiveness' && movement.calendarEventId === sessionId,
  );
}
